import React, { useEffect, useState } from 'react';
import { InputSearch, UniversalFilter } from '@/components';
import { ESCALATION_STATUS_LABEL, EscalationStatus } from '@/types/rental';
import {
  AreaOption,
  FRESHNESS_LABEL,
  FixFreshness,
  TRACKING_NO_VALUE,
  TrackingFilters,
} from '../rentTrackingDisplay';

export interface TrackingFilterBarProps {
  rungs: EscalationStatus[];
  /** Refetches: the rung set is the endpoint's own query parameter. */
  onRungsChange: (rungs: EscalationStatus[]) => void;
  filters: TrackingFilters;
  onFiltersChange: (
    updater: (previous: TrackingFilters) => TrackingFilters
  ) => void;
  onClear: () => void;
  /** Hub, OEM and area values present in the loaded rows. */
  options: {
    hubs: string[];
    oems: string[];
    cities: AreaOption[];
    districts: AreaOption[];
  };
  /** Terms the box offers while typing. */
  suggestions: string[];
  /** Filters the user chose, for the badge. Excludes the default rung set. */
  chosenFilterCount: number;
  visibleCount: number;
  totalCount: number;
  className?: string;
}

const HAS_CASE_OPTIONS = [
  { key: 'with', label: 'Punya case repo', value: 'true' },
  { key: 'without', label: 'Belum ada case repo', value: 'false' },
];

const nameOption = (prefix: string, value: string, emptyLabel: string) => ({
  key: `${prefix}_${value}`,
  label: value === TRACKING_NO_VALUE ? emptyLabel : value,
  value,
});

// Areas filter by code and read by name, so the label cannot double as value.
const areaOption = (prefix: string, area: AreaOption) => ({
  key: `${prefix}_${area.code}`,
  label: area.name,
  value: area.code,
});

/**
 * The search box and the filter popup for both Tracking tabs. Only the rung
 * set goes back to the API; the rest narrows the rows already in memory, so
 * the map and the table always agree on what is shown.
 */
export const TrackingFilterBar: React.FC<TrackingFilterBarProps> = ({
  rungs,
  onRungsChange,
  filters,
  onFiltersChange,
  onClear,
  options,
  suggestions,
  chosenFilterCount,
  visibleCount,
  totalCount,
  className = '',
}) => {
  const [searchInput, setSearchInput] = useState(filters.search);

  // Clearing the filters from the popup has to empty the box too.
  useEffect(() => {
    setSearchInput(filters.search);
  }, [filters.search]);

  const filterConfig = [
    {
      key: 'escalationStatus',
      // Named for what it does — it refetches — so it does not read as a
      // second copy of the legend's "Status unit", which narrows rows already
      // loaded. The two look alike and do different things.
      title: 'Rung yang diambil',
      icon: 'ri-alarm-warning-line',
      multiple: true,
      staticOptions: Object.values(EscalationStatus)
        .filter((status) => status !== EscalationStatus.NORMAL)
        .map((status) => ({
          key: status.toLowerCase(),
          label: ESCALATION_STATUS_LABEL[status],
          value: status,
        })),
    },
    {
      key: 'hub',
      title: 'Hub',
      icon: 'ri-building-2-line',
      multiple: true,
      staticOptions: options.hubs.map((hub) =>
        nameOption('hub', hub, 'Tanpa hub')
      ),
    },
    {
      key: 'oem',
      title: 'OEM',
      icon: 'ri-motorbike-line',
      multiple: true,
      staticOptions: options.oems.map((oem) =>
        nameOption('oem', oem, 'Tanpa OEM')
      ),
    },
    {
      key: 'city',
      title: 'Kabupaten/kota',
      icon: 'ri-map-pin-range-line',
      multiple: true,
      staticOptions: options.cities.map((city) => areaOption('city', city)),
    },
    {
      key: 'district',
      title: 'Kecamatan',
      icon: 'ri-map-pin-2-line',
      multiple: true,
      staticOptions: options.districts.map((district) =>
        areaOption('district', district)
      ),
    },
    {
      key: 'freshness',
      title: 'Umur fix',
      icon: 'ri-time-line',
      multiple: true,
      staticOptions: (Object.keys(FRESHNESS_LABEL) as FixFreshness[]).map(
        (freshness) => ({
          key: freshness,
          label: FRESHNESS_LABEL[freshness],
          value: freshness,
        })
      ),
    },
    {
      key: 'hasCase',
      title: 'Case repossession',
      icon: 'ri-hand-coin-line',
      multiple: true,
      staticOptions: HAS_CASE_OPTIONS,
    },
  ];

  // FilterPopup hands every applied value back as a string, booleans included.
  const currentFilters = {
    escalationStatus: rungs,
    hub: filters.hubs,
    oem: filters.oems,
    city: filters.cities,
    district: filters.districts,
    freshness: filters.freshness,
    hasCase: filters.hasCase.map(String),
  };

  const apply = (applied: Record<string, unknown>) => {
    const list = (key: string) => (applied[key] ?? []) as string[];
    onFiltersChange((previous) => ({
      ...previous,
      hubs: list('hub'),
      oems: list('oem'),
      cities: list('city'),
      districts: list('district'),
      freshness: list('freshness') as FixFreshness[],
      hasCase: list('hasCase').map((value) => value === 'true'),
    }));
    onRungsChange(list('escalationStatus') as EscalationStatus[]);
  };

  const search = (term: string) =>
    onFiltersChange((previous) => ({ ...previous, search: term }));

  return (
    <div className={`flex items-center gap-2 ${className}`}>
      <InputSearch
        placeholder="Cari plat, mitra, area, atau kode"
        value={searchInput}
        onChange={setSearchInput}
        onSearch={search}
        onClear={() => search('')}
        // Picking a suggestion is the whole search, so it applies at once
        // instead of waiting out the debounce.
        suggestions={suggestions}
        onSuggestionClick={(value) => {
          setSearchInput(value);
          search(value);
        }}
        size="small"
        className="flex-1 min-w-0"
      />
      <UniversalFilter
        filters={filterConfig}
        currentFilters={currentFilters}
        onApply={apply}
        onClear={onClear}
        count={chosenFilterCount}
      />
      <span
        className="text-[12px] text-gray-500 whitespace-nowrap"
        title="Jumlah unit yang lolos pencarian dan filter, dari seluruh unit yang dimuat."
      >
        {visibleCount} / {totalCount}
      </span>
    </div>
  );
};

export default TrackingFilterBar;
